import { PlusOutlined } from "@ant-design/icons";
import { Button, Collapse, Modal, Tag } from "antd";
import React, { useState } from "react";
import { $t } from "~/i18n.js";
import { api } from "~/lib/api.js";
import { AddButton } from "../buttons/AddButton.jsx";
import { TimeFormat } from "../date/TimeFormat.jsx";
import { VoucherBalanceStatus } from "./VoucherBalanceStatus.jsx";
import { VoucherInfo } from "./VoucherInfo.jsx";
import { VoucherModal } from "./VoucherModal.jsx";

/**
 * VoucherSelector component
 * @param {object} props
 * @param {object} props.readOnly - If ReadOnly
 * @param {object} props.flight - The flight object
 * @param {object} props.sleepFlight - The sleepFlight object
 * @param {function} props.saveFlight - Function to save flight
 * @param {function} props.saveSleepFlight - Function to save sleepFlight
 */
export function VoucherSelector({
  readOnly,
  flight,
  sleepFlight,
  saveFlight,
  saveSleepFlight,
  size='default'
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [vouchers, setVouchers] = useState([]);
  const [modalVoucher, setModalVoucher] = useState(null);

  const openModal = async () => {
    setModalOpen(true);
    setLoading(true);
    const url = `financial/vouchers.json?page=1&open=1&date=${flight.datum}`;
    const data = await api.get(url);
    setVouchers(data?.results || []);

    setLoading(false);
  };

  const closeModal = () => setModalOpen(false);

  const currentVoucher = flight?.voucher || sleepFlight?.voucher;
  const hasVoucher = currentVoucher && currentVoucher.id !== 'DELETE';
  const voucherIdFlight = flight?.voucher?.id;
  const voucherIdSleep = sleepFlight?.voucher?.id;

  const voucherMismatch =
    voucherIdFlight && voucherIdSleep && voucherIdFlight !== voucherIdSleep;

  const handleSelect = async (voucher) => {
    setLoading(true);
    // ps, we can set the betalend lid irrespective of the voucher
    await saveFlight({
      ...flight,
      voucher,
      voucher_id: voucher.id
    });
    if (sleepFlight)
      await saveSleepFlight({
        ...sleepFlight,
        voucher,
        voucher_id: voucher.id
      });
    setLoading(false);
    setModalOpen(false);
  };

  const handleShowDetails = (voucher, e) => {
    if (e) e.stopPropagation();
    setModalVoucher(voucher);
  };

  //   console.log("flight: ", JSON.stringify(flight, null, 2));
  //   console.log("sleep: ", JSON.stringify(sleepFlight, null, 2));

  return (
    <section>
      {voucherMismatch && <Tag color="red">{$t("Bon niet gekoppeld")}</Tag>}

      <div>
        {!hasVoucher ? (
          <AddButton disabled={readOnly} onClick={openModal} size={size} />
        ) : (
          <Button
            type='dashed'
            size={size}
            onClick={() => handleShowDetails(currentVoucher)}
            style={{ width: 100 }}
          >
            {currentVoucher?.name?.substring(0, 10) || $t("Bon")}
          </Button>
        )}
        <Modal
          open={modalOpen}
          onCancel={closeModal}
          footer={null}
          loading={loading}
          title={$t('Selecteer bon')}
        >
          {vouchers.length >= 1 && (
            <Collapse accordion>
              {vouchers.map((voucher) => (
                <Collapse.Panel
                  key={voucher.id}
                  header={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                      }}
                    >
                      <VoucherBalanceStatus balance={voucher.ledger_balance} />
                      <span style={{ marginRight: 16 }}>
                        { voucher.reserved_timeslot
                           ? <TimeFormat date={voucher.reserved_timeslot} />
                           : <Tag>{$t('flex')}</Tag>
                        }
                      </span>
                      <span style={{ fontWeight: 500, marginRight: 16 }}>
                        {voucher.prepaid_product?.name}
                      </span>
                      <span style={{ color: "#888" }}>{voucher.name}</span>
                    </div>
                  }
                  extra={
                    <Button
                      type="primary"
                      shape="circle"
                      onClick={() => handleSelect(voucher)}
                      icon={<PlusOutlined />}
                    />
                  }
                >
                  <VoucherInfo voucher={voucher} />
                  <Button
                    type="primary"
                    style={{ marginTop: 12 }}
                    onClick={() => handleSelect(voucher)}
                    icon={<PlusOutlined />}
                    block
                  >
                    {$t("Koppel deze bon")}
                  </Button>
                </Collapse.Panel>
              ))}
            </Collapse>
          )}
        </Modal>
        <VoucherModal
          open={!!modalVoucher}
          voucher={modalVoucher}
          fetchRemainingData={true}
          onClose={() => setModalVoucher(null)}
          disconnectVoucher={() => handleSelect({ id: "DELETE" })} // Disconnect voucher
          readOnly={readOnly}
        />
      </div>
    </section>
  );
}
